import PropTypes from 'prop-types';
import React from 'react';
import { Image } from 'react-bootstrap';
import Carousel from 'nuka-carousel';
import Arrow from './Arrow';
import imageResizer, { clearImageCaches } from '../helpers/imageResizer';
import { handleGalleryToolbarVisibility } from '../helpers/visibility';
import './Slider.scss';

const THREE_UP_RATIO = 1.2;

/*
	SERVER SIDE RENDERING SIZES
	===============================
	#1 Desktop Screen Size: 1920x1080
	#1 Mobile Screen Size: 414x896
*/

// default heights
const DEFAULT_IMAGE_HEIGHT_DESKTOP = 502; // at 1920x1080, this is 3-up height per image
const DEFAULT_IMAGE_HEIGHT_MOBILE = 250;

// default widths
const DEFAULT_IMAGE_WIDTH_DESKTOP = 767; // at 1920x1080, this is 3-up width per image
const DEFAULT_IMAGE_WIDTH_MOBILE = 414;

class Slider extends React.Component {
	sliderRef = React.createRef();

	constructor(props) {
		super(props);
		const {
			innerWidth,
			currentIndex,
			widgetInfo: {
				vehicle: {
					make,
					model,
					year,
					type,
					bodyStyle,
					trim,
					certified,
					imgAttributes
				}
			},
			labels,
			idcImgAttrFlag,
			threeUpDisplay
		} = props;
		const typeTerm = type ? labels.get(certified ? 'CERTIFIED' : type) : '';
		const typeTermCapitalCase = typeTerm
			? typeTerm[0].toUpperCase() + typeTerm.substr(1).toLowerCase()
			: '';
		this.imgAlt =
			`${typeTermCapitalCase} ${year} ${make} ${model} ${trim} ${bodyStyle}`.trim();
		this.imgTitle =
			`${typeTermCapitalCase} ${year} ${make} ${model} ${trim} ${bodyStyle}`.trim();
		if (idcImgAttrFlag) {
			this.imgAlt = imgAttributes?.imageAlt;
			this.imgTitle = imgAttributes?.imageTitle;
		}

		this.state = {
			loadedIndex: threeUpDisplay ? 3 : 1,
			viewportWidth: innerWidth,
			currentIndex: currentIndex || 0,
			adaptiveHeight: false
		};

		// TESTING SERVER SIDE RENDERING
		// determine whether this is mobile or desktop,
		// choose the correct height/width
		const {
			widgetInfo: { deviceType }
		} = props;
		this.DEFAULT_IMAGE_HEIGHT =
			deviceType === 'DESKTOP'
				? DEFAULT_IMAGE_HEIGHT_DESKTOP
				: DEFAULT_IMAGE_HEIGHT_MOBILE;
		this.DEFAULT_IMAGE_WIDTH =
			deviceType === 'DESKTOP'
				? DEFAULT_IMAGE_WIDTH_DESKTOP
				: DEFAULT_IMAGE_WIDTH_MOBILE;
	}

	// eslint-disable-next-line
	UNSAFE_componentWillReceiveProps(nextProps) {
		// Set a unique prop for image width so that images don't
		// repaint when the state updates the innerWidth property.
		const newState = { ...this.state };

		const { adaptiveHeight } = this.state;

		const { innerWidth } = nextProps;

		if (!newState.viewportWidth) {
			newState.viewportWidth = innerWidth;
		}

		if (newState.currentIndex !== nextProps.currentIndex) {
			newState.currentIndex = nextProps.currentIndex;

			// if index changes, assume gallery was interacted with, load all images
			newState.loadedIndex = nextProps.images.length;
		}

		this.setState(newState, () => {
			// Turn on adaptiveHeight on user interaction
			// On initial load it will stuck at zero height if adaptiveHeight on becasue image dimension not exist yet
			if (!adaptiveHeight) {
				this.setState({ adaptiveHeight: true });
			}
		});
	}

	shouldComponentUpdate(nextProps, nextState) {
		// don't care about the method changes (these should maybe be updated to dispatched actions)
		const ignoreProps = ['onCurrentIndexChange', 'onPhotoClick', 'trackGa'];

		const nextPropsKeys = Object.keys(nextProps);
		let hasNewProps = false;
		nextPropsKeys.forEach((propKey) => {
			if (
				ignoreProps.indexOf(propKey) === -1 &&
				nextProps[propKey] !== this.props[propKey]
			) {
				hasNewProps = true;
			}
		});

		const nextStateKeys = Object.keys(nextState);
		let hasNewState = false;
		nextStateKeys.forEach((stateKey) => {
			if (
				stateKey !== 'currentIndex' &&
				nextState[stateKey] !== this.state[stateKey]
			) {
				hasNewState = true;
			}
		});

		return hasNewProps || hasNewState;
	}

	componentWillUnmount() {
		// Clean up cache memory
		clearImageCaches();
	}

	getStyle = (numSlides, slideHeight) => {
		if (numSlides === 1) {
			// Sometime it stucks at zero height if adaptiveHeight on becasue image dimension not exist yet
			return { height: 'auto' };
		}

		if (slideHeight) {
			return { height: slideHeight };
		}

		return {};
	};

	render() {
		const { currentIndex, viewportWidth, adaptiveHeight } = this.state;
		const {
			threeUpDisplay,
			imageMode,
			responsiveImage,
			onPhotoClick,
			trackGa,
			images,
			widgetInfo: { deviceType },
			onCurrentIndexChange,
			updateIsBeingDragged,
			isBeingDragged,
			disableAnimation,
			modalSettings: { modalIsOpen },
			sliderHeight,
			onImageError,
			updateSliderHeight
		} = this.props;

		const numSlides = images.length;
		const slidesToShow = threeUpDisplay ? 3 / THREE_UP_RATIO : 1;
		// Only show arrows if needed
		const isDesktop = deviceType === 'DESKTOP';
		const canScroll = numSlides > 2 || (!threeUpDisplay && numSlides > 1);
		const showArrows = canScroll && isDesktop;

		const sliderStyle = this.getStyle(numSlides, sliderHeight);

		const nextArrow = (arrowProps) => (
			<Arrow
				direction="right"
				type="next"
				onClickEvent={() => {
					arrowProps.nextSlide();
					updateIsBeingDragged(false);
					trackGa({
						element: 'Photo Carousel Next button',
						result: 'Move to next photo',
						dataToExcludeFromWidgetUserEvent: {
							currentItem: currentIndex + 1,
							totalItems: numSlides
						}
					});
				}}
			/>
		);
		const prevArrow = (arrowProps) => (
			<Arrow
				direction="left"
				type="prev"
				onClickEvent={() => {
					arrowProps.previousSlide();
					updateIsBeingDragged(false);
					trackGa({
						element: 'Photo Carousel Previous button',
						result: 'Move to previous photo',
						dataToExcludeFromWidgetUserEvent: {
							currentItem: currentIndex + 1,
							totalItems: numSlides
						}
					});
				}}
			/>
		);
		const sliderProps = {
			enableKeyboardControls: false,
			afterSlide: (index) => {
				if (!modalIsOpen && isBeingDragged) {
					trackGa({
						dataToExcludeFromWidgetUserEvent: {
							currentItem: currentIndex + 1,
							totalItems: numSlides
						},
						element: 'Photo Carousel',
						action: 'swiped',
						result: `Moved to photo ${index + 1}`
					});
					updateIsBeingDragged(false);
				}

				updateSliderHeight();
			},
			onDragStart: () => {
				updateIsBeingDragged(true);
			},
			wrapAround: canScroll,
			disableAnimation,
			speed: 500,
			slideIndex: canScroll ? currentIndex : 0,
			innerRef: this.sliderRef,
			slidesToShow,
			slidesToScroll: 1,
			dragging: showArrows,
			swiping: canScroll,
			adaptiveHeight,
			style: sliderStyle,
			beforeSlide: (current, newIndex) => {
				if (newIndex !== this.props.currentIndex) {
					onCurrentIndexChange(newIndex, (callbackIndex) => {
						handleGalleryToolbarVisibility(images, callbackIndex);
					});
				}
			},
			renderBottomCenterControls: null,
			renderCenterLeftControls: showArrows ? prevArrow : null,
			renderCenterRightControls: showArrows ? nextArrow : null,
			renderAnnounceSlideMessage: ({ currentSlide, count }) =>
				`${this.imgAlt} Photo ${currentSlide + 1} of ${count}`
		};

		const photoClickHandler = (index) => {
			onPhotoClick(index);
		};

		const classes = [];
		if (numSlides === 1 && !imageMode) {
			classes.push('single-slide');
		}

		const blankImage = '/static/images/blank.gif';
		let firstImageSrc = blankImage;

		const slides = images.map((image, index) => {
			const { src } = image;
			const loaded = index <= this.state.loadedIndex;

			let imageSrc = blankImage;
			if (loaded) {
				imageSrc = imageResizer(
					threeUpDisplay,
					viewportWidth,
					src,
					THREE_UP_RATIO,
					deviceType
				);
			} else {
				classes.push('d-none');
			}

			// Grab the first image for the background
			if (firstImageSrc === blankImage) {
				firstImageSrc = imageSrc;
			}

			const style = {
				background: `url(${imageSrc}) no-repeat center/cover`,
				// TESTING SERVER SIDE RENDERING
				height: `${this.DEFAULT_IMAGE_HEIGHT}px`
			};

			const onImageErrorCallback = () => {
				onImageError(src, imageSrc);
			};

			let imageComponent;
			if (imageMode) {
				imageComponent = (
					<Image
						key={imageSrc}
						src={imageSrc}
						className={classes.join(' ')}
						onError={onImageErrorCallback}
						onClick={() => {
							photoClickHandler(index);
						}}
						onLoad={() => {
							updateSliderHeight();
						}}
						role="presentation"
						alt=""
						title={this.imgTitle}
						fetchpriority={index === 0 ? 'high' : 'low'}
					/>
				);
				if (!responsiveImage && !threeUpDisplay) {
					// Wrap component to use the image as its own background
					imageComponent = (
						<div
							className="blurred-container"
							key={`container-${imageSrc}`}
						>
							<div
								style={style}
								className="blurred-background"
								aria-label={this.imgAlt}
							/>
							{imageComponent}
						</div>
					);
				}
			} else {
				imageComponent = (
					<div
						key={imageSrc}
						style={style}
						className={classes.join(' ')}
						onClick={() => {
							photoClickHandler(index);
						}}
						role="presentation"
						aria-label={this.imgAlt}
					>
						<Image
							src={imageSrc}
							className="d-none"
							onError={onImageErrorCallback}
						/>
					</div>
				);
			}
			return imageComponent;
		});

		const carouselClasses = [
			'photo-carousel',
			!imageMode ? 'background-mode' : 'image-mode',
			threeUpDisplay ? 'three-up-display' : ''
		];

		let carouselComponent;
		if (!responsiveImage && threeUpDisplay) {
			// Use first image for background
			const carouselStyle = {
				background: `url(${firstImageSrc}) no-repeat right/contain`,
				// TESTING SERVER SIDE RENDERING
				height: `${this.DEFAULT_IMAGE_HEIGHT}px`
			};
			carouselComponent = (
				// Add blurred background
				<div className="blurred-container">
					<div style={carouselStyle} className="blurred-background" />
					<div className={carouselClasses.join(' ')}>
						<Carousel {...sliderProps}>{slides}</Carousel>
					</div>
				</div>
			);
		} else {
			carouselComponent = (
				<div className={carouselClasses.join(' ')}>
					<Carousel {...sliderProps}>{slides}</Carousel>
				</div>
			);
		}
		return carouselComponent;
	}
}

Slider.defaultProps = {
	currentIndex: 0,
	nextSlide: () => {},
	previousSlide: () => {},
	onPhotoClick: () => {},
	updateIsBeingDragged: () => {},
	isBeingDragged: false,
	modalSettings: {
		modalIsOpen: false
	},
	onImageError: () => {}
};

Slider.propTypes = {
	labels: PropTypes.shape({
		has: PropTypes.func,
		get: PropTypes.func,
		getKeys: PropTypes.func,
		getAll: PropTypes.func,
		getHTML: PropTypes.func
	}).isRequired,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			thumbnail: PropTypes.string
		})
	).isRequired,
	modalSettings: PropTypes.shape({
		modalIsOpen: PropTypes.bool
	}),

	// renderSettings
	preloadImages: PropTypes.arrayOf(
		PropTypes.shape({
			path: PropTypes.string.isRequired
		})
	),
	innerHeight: PropTypes.number.isRequired,
	sliderHeight: PropTypes.number,
	updateSliderHeight: PropTypes.func.isRequired,
	innerWidth: PropTypes.number.isRequired,
	imageMode: PropTypes.bool.isRequired,
	responsiveImage: PropTypes.bool.isRequired,
	threeUpDisplay: PropTypes.bool.isRequired,
	currentIndex: PropTypes.number,
	onCurrentIndexChange: PropTypes.func.isRequired,
	nextSlide: PropTypes.func,
	previousSlide: PropTypes.func,
	trackGa: PropTypes.func.isRequired,
	onPhotoClick: PropTypes.func,
	widgetInfo: PropTypes.shape({
		deviceType: PropTypes.string,
		vehicle: PropTypes.shape({
			make: PropTypes.string,
			model: PropTypes.string,
			year: PropTypes.string,
			type: PropTypes.string,
			bodyStyle: PropTypes.string,
			trim: PropTypes.string,
			certified: PropTypes.bool,
			imgAttributes: PropTypes.shape({
				imageAlt: PropTypes.string,
				imageTitle: PropTypes.string
			})
		}).isRequired
	}).isRequired,
	updateIsBeingDragged: PropTypes.func,
	isBeingDragged: PropTypes.bool,
	disableAnimation: PropTypes.bool,
	onImageError: PropTypes.func,
	idcImgAttrFlag: PropTypes.bool.isRequired
};

export default Slider;
